/**
 * Ruby AI Action editor model. PURE — no React, no I/O.
 *
 * An ACTION# row is the price and model of one thing a tenant can ask Ruby
 * to do (spec D3): default model, fallback model, base multiplier, and a
 * SEPARATE minimum and maximum charge in Ruby Tokens. The maximum is also
 * what the wallet reserves before the vendor is called.
 */
import type { AiModelDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiActionStepDto } from '../../../services/aiControl/rubyAi';
import {
  findModel, isPriced, modelHasCapability, modelMatchesGroup, modelRef, modelsWithCapability,
  parseModelRef, GROUP_LABELS,
} from './catalogModel';

/**
 * One step of a multi-step action (2026-10-04, Ruby Generation Engine), e.g.
 * image.single_object's "draw" and "background" steps. A single fallback —
 * the UI offers one "If it fails" picker per step, not a list; a step with no
 * UI control for its fallback (the background step today) keeps whatever
 * fallback the row already had, since `buildActionPatch` only ever writes
 * back what the draft carries.
 */
export interface ActionStepDraft {
  id: string;
  capability: string;
  /** "providerId#modelId" */
  primary: string;
  /** "providerId#modelId" or '' for none. */
  fallback: string;
}

export interface ActionDraft {
  actionId: string;
  enabled: boolean;
  /** "providerId#modelId" */
  primary: string;
  /** "providerId#modelId" or '' for none. */
  fallback: string;
  multiplier: string;
  minTokens: string;
  maxTokens: string;
  /** Multi-step pipeline; empty = single-step via primary/fallback above. */
  steps: ActionStepDraft[];
}

export function buildActionDraft(a: AiActionDto): ActionDraft {
  return {
    actionId: a.actionId,
    enabled: a.enabled,
    primary: modelRef(a.providerId, a.modelId),
    fallback: modelRef(a.fallbackProviderId, a.fallbackModelId),
    multiplier: String(a.multiplier ?? ''),
    minTokens: String(a.minTokens ?? ''),
    maxTokens: String(a.maxTokens ?? ''),
    steps: (a.steps ?? []).map((s) => ({
      id: s.id,
      capability: s.capability,
      primary: modelRef(s.providerId, s.modelId),
      fallback: modelRef(s.fallbacks?.[0]?.providerId, s.fallbacks?.[0]?.modelId),
    })),
  };
}

const num = (s: string): number => (s.trim() === '' ? NaN : Number(s));

export interface DraftIssues {
  errors: string[];
  warnings: string[];
}

/**
 * Errors block Save; warnings are shown but do not.
 *   - min ≤ max, both whole Ruby Tokens, max > 0
 *   - multiplier 0–50 (below 1 = selling under cost: a warning)
 *   - models must exist and match the action's group ("used for")
 *   - fallback ≠ primary
 *   - an ENABLED action needs a priced primary (an unpriced call is unmetered)
 */
export function validateActionDraft(
  draft: ActionDraft,
  action: Pick<AiActionDto, 'group'>,
  models: AiModelDto[],
  vocabulary: readonly string[],
): DraftIssues {
  const errors: string[] = [];
  const warnings: string[] = [];
  const group = GROUP_LABELS[action.group] ?? action.group;

  const mult = num(draft.multiplier);
  if (!Number.isFinite(mult) || mult < 0 || mult > 50) errors.push('Base multiplier must be a number from 0 to 50.');
  else if (mult < 1) warnings.push('A multiplier below 1 × charges tenants less than the vendor charges us.');

  const min = num(draft.minTokens);
  const max = num(draft.maxTokens);
  if (!Number.isInteger(min) || min < 0) errors.push('Minimum charge must be a whole number of Ruby Tokens (0 or more).');
  if (!Number.isInteger(max) || max <= 0) errors.push('Maximum charge must be a whole number of Ruby Tokens above 0.');
  if (Number.isInteger(min) && Number.isInteger(max) && min > max) {
    errors.push('Minimum charge cannot be more than the maximum charge.');
  }

  const p = parseModelRef(draft.primary);
  if (!p) {
    errors.push('Choose a default model.');
  } else {
    const m = findModel(models, p.providerId, p.modelId);
    if (!m) errors.push(`Default model ${p.providerId}/${p.modelId} is not in the catalogue.`);
    else {
      if (!modelMatchesGroup(m, action.group, vocabulary)) {
        errors.push(`${m.label} is not marked for ${group} on Providers & Models.`);
      }
      if (!isPriced(m)) {
        (draft.enabled ? errors : warnings).push(`${m.label} has no vendor price — an unpriced call cannot be charged.`);
      }
      if (!m.enabled) warnings.push(`${m.label} is disabled — runs will use the fallback.`);
    }
  }

  const f = parseModelRef(draft.fallback);
  if (f) {
    if (draft.fallback === draft.primary) {
      errors.push('The fallback model must be different from the default model.');
    } else {
      const m = findModel(models, f.providerId, f.modelId);
      if (!m) errors.push(`Fallback model ${f.providerId}/${f.modelId} is not in the catalogue.`);
      else {
        if (!modelMatchesGroup(m, action.group, vocabulary)) {
          errors.push(`Fallback ${m.label} is not marked for ${group} on Providers & Models.`);
        }
        if (!isPriced(m)) warnings.push(`Fallback ${m.label} has no vendor price.`);
      }
    }
  } else if (draft.enabled) {
    warnings.push('No fallback model — if the default model fails, the run fails.');
  }

  for (const step of draft.steps) {
    const sp = parseModelRef(step.primary);
    if (!sp) {
      errors.push(`Choose a model for the "${step.id}" step.`);
    } else {
      const m = findModel(models, sp.providerId, sp.modelId);
      if (!m) errors.push(`${step.id} step model ${sp.providerId}/${sp.modelId} is not in the catalogue.`);
      else {
        if (!modelHasCapability(m, step.capability, vocabulary)) {
          errors.push(`${m.label} is not marked for the "${step.id}" step's capability on Providers & Models.`);
        }
        if (!isPriced(m)) (draft.enabled ? errors : warnings).push(`${m.label} has no vendor price — an unpriced call cannot be charged.`);
        if (!m.enabled) warnings.push(`${m.label} is disabled — the "${step.id}" step will use its fallback.`);
      }
    }
    const sf = parseModelRef(step.fallback);
    if (sf) {
      if (step.fallback === step.primary) {
        errors.push(`The "${step.id}" step's fallback must be different from its default model.`);
      } else {
        const m = findModel(models, sf.providerId, sf.modelId);
        if (!m) errors.push(`${step.id} step fallback ${sf.providerId}/${sf.modelId} is not in the catalogue.`);
        else if (!modelHasCapability(m, step.capability, vocabulary)) {
          errors.push(`Fallback ${m.label} is not marked for the "${step.id}" step's capability.`);
        }
      }
    }
  }

  return { errors, warnings };
}

const FIELDS: Array<keyof ActionDraft> = ['enabled', 'primary', 'fallback', 'multiplier', 'minTokens', 'maxTokens'];

const stepsEqual = (a: ActionStepDraft[], b: ActionStepDraft[]): boolean =>
  a.length === b.length && a.every((s, i) => s.id === b[i].id && s.capability === b[i].capability && s.primary === b[i].primary && s.fallback === b[i].fallback);

export function actionDraftDirty(original: ActionDraft, draft: ActionDraft): boolean {
  return FIELDS.some((k) => original[k] !== draft[k]) || !stepsEqual(original.steps, draft.steps);
}

/**
 * Merge patch for PUT /agents/admin/ai/actions/{id}: only changed fields.
 * A removed fallback is sent as `null` on both ids — `undefined` would be
 * dropped by JSON and the stale fallback would survive the save.
 */
export function buildActionPatch(original: ActionDraft, draft: ActionDraft): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (draft.enabled !== original.enabled) patch.enabled = draft.enabled;
  if (draft.primary !== original.primary) {
    const p = parseModelRef(draft.primary);
    if (p) { patch.providerId = p.providerId; patch.modelId = p.modelId; }
  }
  if (draft.fallback !== original.fallback) {
    const f = parseModelRef(draft.fallback);
    patch.fallbackProviderId = f ? f.providerId : null;
    patch.fallbackModelId = f ? f.modelId : null;
  }
  if (draft.multiplier !== original.multiplier) patch.multiplier = Number(draft.multiplier);
  if (draft.minTokens !== original.minTokens) patch.minTokens = Number(draft.minTokens);
  if (draft.maxTokens !== original.maxTokens) patch.maxTokens = Number(draft.maxTokens);
  if (!stepsEqual(original.steps, draft.steps)) {
    patch.steps = draft.steps.map((s): AiActionStepDto => {
      const p = parseModelRef(s.primary);
      const f = parseModelRef(s.fallback);
      return {
        id: s.id,
        capability: s.capability,
        providerId: p?.providerId ?? '',
        modelId: p?.modelId ?? '',
        fallbacks: f ? [{ providerId: f.providerId, modelId: f.modelId }] : [],
      };
    });
  }
  return patch;
}

/** Picker options: models matching the group, plus the current value if it does not (so it never blanks). */
export function actionModelOptions(
  models: AiModelDto[],
  action: Pick<AiActionDto, 'group'>,
  vocabulary: readonly string[],
  current: string,
  providerLabel: (providerId: string) => string,
  priceLabel: (m: AiModelDto) => string,
): Array<{ value: string; label: string; subtitle?: string }> {
  const opts = models
    .filter((m) => modelMatchesGroup(m, action.group, vocabulary))
    .map((m) => ({
      value: modelRef(m.providerId, m.modelId),
      label: m.enabled ? m.label : `${m.label} (disabled)`,
      subtitle: `${providerLabel(m.providerId)} · ${priceLabel(m)}`,
    }));
  if (current && !opts.some((o) => o.value === current)) {
    const p = parseModelRef(current);
    const m = p ? findModel(models, p.providerId, p.modelId) : undefined;
    opts.unshift({
      value: current,
      label: `${m?.label ?? current} (not marked for this group)`,
      subtitle: 'Mark it on Providers & Models, or pick another model',
    });
  }
  return opts;
}

/**
 * Picker options for a step's single capability — only models with that
 * capability are offered. A disabled model is kept in the list (not hidden)
 * but marked "(Off)": the owner may still want to see it is wired, just
 * switched off, and it keeps the picker from blanking if it is the saved
 * value. The current value, even if it no longer carries the capability, is
 * kept visible so a save never silently blanks a field it didn't touch.
 */
export function stepModelOptions(
  models: AiModelDto[],
  capability: string,
  vocabulary: readonly string[],
  current: string,
  providerLabel: (providerId: string) => string,
  priceLabel: (m: AiModelDto) => string,
): Array<{ value: string; label: string; subtitle?: string }> {
  const opts = modelsWithCapability(models, capability, vocabulary).map((m) => ({
    value: modelRef(m.providerId, m.modelId),
    label: m.enabled ? m.label : `${m.label} (Off)`,
    subtitle: `${providerLabel(m.providerId)} · ${priceLabel(m)}`,
  }));
  if (current && !opts.some((o) => o.value === current)) {
    const p = parseModelRef(current);
    const m = p ? findModel(models, p.providerId, p.modelId) : undefined;
    opts.unshift({
      value: current,
      label: `${m?.label ?? current} (not marked for this step)`,
      subtitle: 'Mark it on Providers & Models, or pick another model',
    });
  }
  return opts;
}
